/**
 * GEXChartV2 — TanukiTrade-style Gamma Exposure visualization
 *
 * Layout:
 *   ┌─ Toolbar: symbol | expiration tabs | interval ─┐
 *   ├─ Metrics: spot | netGEX | flip | callWall | putWall | regime ─┤
 *   ├─────────────── price chart ──────────────┬── GEX profile ──┤
 *   │  candles + level lines (CW, PW, Flip, ZG)│  horizontal bars │
 *   └───────────────────────────────────────────┴─────────────────┘
 *
 * Replaces the cluttered 3-view / 10-overlay GEXChart with a single focused view.
 * Objectives: where are the walls, where is the flip, what's the regime.
 */
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { usePriceData } from "./hooks/usePriceData";
import { useGammaData } from "./hooks/useGammaData";
import { useGexChainLevels } from "./hooks/useGexChainLevels";
import { useFuturesAwareSymbol } from "../../../hooks/useFuturesAwareSymbol";
import {
  getGexUnderlying,
  getPriceSourceLabel,
} from "../../../services/symbolPricingService";
import type { GEXProfileMode } from "./types";
import { useChartTheme } from "../../../theme/chartTheme";

type ExpiryTab = "0dte" | "weekly" | "monthly" | "all";

const EXPIRY_TABS: { id: ExpiryTab; label: string; profileMode: GEXProfileMode }[] = [
  { id: "0dte", label: "0DTE", profileMode: "latest" },
  { id: "weekly", label: "Weekly", profileMode: "full" },
  { id: "monthly", label: "Monthly", profileMode: "full" },
  { id: "all", label: "All", profileMode: "full" },
];

const INTERVALS = ["1m", "5m", "15m", "1h", "1d"];

// Canvas fonts cannot read CSS custom properties; mirror the token stacks here.
const MONO_FONT = '"IBM Plex Mono", ui-monospace, monospace';
const UI_FONT = '"IBM Plex Sans", system-ui, sans-serif';

// Level colors (TanukiTrade-inspired palette). Chrome (background, grid, text)
// comes from the active palette via useChartTheme at draw time.
const COLORS = {
  candleUp: "#26a69a",
  candleDown: "#ef5350",
  callWall: "#ef5350",     // red — resistance
  putWall: "#26a69a",      // green — support
  gammaFlip: "#ffc107",    // yellow/amber — pivot
  zeroGamma: "#9e9e9e",    // gray — neutral
  spot: "#42a5f5",         // blue
  posGex: "#26a69a",       // green (positive gamma = stable)
  negGex: "#ef5350",       // red (negative gamma = volatile)
  positive: "#26a69a",
  negative: "#ef5350",
};

interface Props {
  data?: unknown[];
  dataMode?: string;
  /** Starting symbol (raw alias, e.g. SPX). Used by the mobile embed; the
   * in-chart symbol input still works after mount. */
  initialSymbol?: string;
  initialInterval?: string;
}

export default function GEXChartV2(_props: Props) {
  const [symbol, setSymbol] = useState(
    () => (_props.initialSymbol || "SPY").toUpperCase(),
  );
  const [interval, setIntervalState] = useState(
    () =>
      INTERVALS.includes(_props.initialInterval || "")
        ? (_props.initialInterval as string)
        : "5m",
  );
  const [expiryTab, setExpiryTab] = useState<ExpiryTab>("weekly");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const profileCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const ct = useChartTheme();
  const [, setHover] = useState<{ x: number; y: number } | null>(null);
  const [size, setSize] = useState({ w: 1200, h: 600 });
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    rows: { label: string; value: string; color?: string }[];
  } | null>(null);

  const gexUnderlying = getGexUnderlying(symbol);
  const { fetchSymbol, displaySymbol } = useFuturesAwareSymbol(symbol);
  const priceLabel = getPriceSourceLabel(symbol);

  const profileMode = useMemo(
    () => EXPIRY_TABS.find((t) => t.id === expiryTab)?.profileMode || "full",
    [expiryTab],
  );

  const { chainLevels, chainSpot, loading: chainLoading } =
    useGexChainLevels(gexUnderlying);

  const {
    candles,
    loading: priceLoading,
    liveSpot,
    wsStatus,
    chartScale,
    barsError,
  } = usePriceData(fetchSymbol, interval, chainSpot || 0);

  const {
    gammaData,
    gammaLevels,
    metrics,
    loading: gammaLoading,
  } = useGammaData(fetchSymbol, profileMode);

  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) {
        setSize({
          w: e.contentRect.width,
          h: Math.max(400, e.contentRect.height),
        });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Merge chain levels with gamma levels (prefer chain for CW/PW/Flip)
  const allLevels = useMemo(() => {
    const merged = new Map<string, { strike: number; type: string }>();
    for (const l of gammaLevels) {
      merged.set(l.type, { strike: l.strike, type: l.type });
    }
    for (const l of chainLevels) {
      merged.set(l.type, { strike: l.strike, type: l.type });
    }
    return Array.from(merged.values());
  }, [gammaLevels, chainLevels]);

  const callWall = useMemo(() => {
    const cw = allLevels.find((l) => l.type === "call_wall");
    if (cw) return cw.strike;
    const max = gammaData.reduce(
      (best, b) => (b.callGex > best.callGex ? b : best),
      gammaData[0],
    );
    return max?.strike || null;
  }, [allLevels, gammaData]);

  const putWall = useMemo(() => {
    const pw = allLevels.find((l) => l.type === "put_wall");
    if (pw) return pw.strike;
    const max = gammaData.reduce(
      (best, b) => (b.putGex < best.putGex ? b : best),
      gammaData[0],
    );
    return max?.strike || null;
  }, [allLevels, gammaData]);

  const gammaFlip = useMemo(() => {
    const gf = allLevels.find((l) => l.type === "gamma_flip" || l.type === "zero_gamma");
    if (gf) return gf.strike;
    return metrics.zeroGamma;
  }, [allLevels, metrics]);

  const displaySpot = liveSpot > 0 ? liveSpot : metrics.spot * (chartScale || 1);
  const netGex = metrics.netGex;
  const regime = netGex > 0 ? "POSITIVE (stable)" : netGex < 0 ? "NEGATIVE (volatile)" : "NEUTRAL";
  const regimeColor = netGex > 0 ? COLORS.positive : netGex < 0 ? COLORS.negative : ct.text;

  // ── Canvas rendering ──────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const profileCanvas = profileCanvasRef.current;
    if (!canvas || !profileCanvas) return;

    const dpr = window.devicePixelRatio || 1;
    const profileWidth = Math.min(280, size.w * 0.25);
    const priceWidth = size.w - profileWidth - 12;
    const chartHeight = size.h;

    // Setup price canvas
    canvas.width = priceWidth * dpr;
    canvas.height = chartHeight * dpr;
    canvas.style.width = `${priceWidth}px`;
    canvas.style.height = `${chartHeight}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, priceWidth, chartHeight);

    // Setup profile canvas
    profileCanvas.width = profileWidth * dpr;
    profileCanvas.height = chartHeight * dpr;
    profileCanvas.style.width = `${profileWidth}px`;
    profileCanvas.style.height = `${chartHeight}px`;
    const pctx = profileCanvas.getContext("2d");
    if (!pctx) return;
    pctx.scale(dpr, dpr);
    pctx.clearRect(0, 0, profileWidth, chartHeight);

    if (candles.length === 0) {
      ctx.fillStyle = ct.text;
      ctx.font = `14px ${UI_FONT}`;
      ctx.textAlign = "center";
      ctx.fillText(
        priceLoading ? "Loading price data…" : `No price data for ${displaySymbol}`,
        priceWidth / 2,
        chartHeight / 2,
      );
      // Say why, so an upstream hiccup is not mistaken for a symbol that has no data.
      if (!priceLoading && barsError) {
        ctx.fillStyle = COLORS.gammaFlip;
        ctx.font = `11px ${UI_FONT}`;
        const words = barsError.split(" ");
        const lines: string[] = [];
        let line = "";
        for (const w of words) {
          const next = line ? `${line} ${w}` : w;
          if (ctx.measureText(next).width > priceWidth - 40 && line) {
            lines.push(line);
            line = w;
          } else {
            line = next;
          }
        }
        if (line) lines.push(line);
        lines.slice(0, 3).forEach((l, i) => {
          ctx.fillText(l, priceWidth / 2, chartHeight / 2 + 22 + i * 15);
        });
      }
      return;
    }

    // ── Price chart scales ──
    const padding = { top: 10, right: 60, bottom: 24, left: 8 };
    const plotW = priceWidth - padding.left - padding.right;
    const plotH = chartHeight - padding.top - padding.bottom;

    // Price range from candles + levels
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    for (const c of candles) {
      minPrice = Math.min(minPrice, c.l);
      maxPrice = Math.max(maxPrice, c.h);
    }
    // Include gamma levels in the range, but only those near enough to matter. A wall 20% away
    // would otherwise compress every candle into a band a few pixels tall to make room for a line
    // price will not reach; drawLevel already drops whatever falls outside the plot.
    const RANGE_BAND = 0.08;
    const anchor = displaySpot > 0 ? displaySpot : (minPrice + maxPrice) / 2;
    const levelStrikes = [callWall, putWall, gammaFlip, displaySpot].filter(
      (v): v is number =>
        v != null && v > 0 && Math.abs(v - anchor) / anchor <= RANGE_BAND,
    );
    for (const lv of levelStrikes) {
      minPrice = Math.min(minPrice, lv);
      maxPrice = Math.max(maxPrice, lv);
    }
    if (minPrice === Infinity || maxPrice === -Infinity) return;
    const priceRange = maxPrice - minPrice;
    minPrice -= priceRange * 0.05;
    maxPrice += priceRange * 0.05;

    const yPrice = (p: number) =>
      padding.top + ((maxPrice - p) / (maxPrice - minPrice)) * plotH;

    // X scale
    const tMin = candles[0].t;
    const tMax = candles[candles.length - 1].t;
    const tRange = Math.max(1, tMax - tMin);
    const xScale = (t: number) =>
      padding.left + ((t - tMin) / tRange) * plotW;

    const candleW = Math.max(1, Math.min(8, (plotW / candles.length) * 0.7));

    // ── Grid ──
    ctx.strokeStyle = ct.grid;
    ctx.lineWidth = 1;
    ctx.fillStyle = ct.text;
    ctx.font = `10px ${MONO_FONT}`;
    ctx.textAlign = "left";
    const gridSteps = 6;
    for (let i = 0; i <= gridSteps; i++) {
      const p = minPrice + ((maxPrice - minPrice) * i) / gridSteps;
      const y = yPrice(p);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + plotW, y);
      ctx.stroke();
      ctx.fillText(p.toFixed(2), padding.left + plotW + 4, y + 3);
    }

    // ── Candles ──
    for (const c of candles) {
      const x = xScale(c.t);
      const yO = yPrice(c.o);
      const yC = yPrice(c.c);
      const yH = yPrice(c.h);
      const yL = yPrice(c.l);
      const isUp = c.c >= c.o;
      ctx.strokeStyle = isUp ? COLORS.candleUp : COLORS.candleDown;
      ctx.fillStyle = isUp ? COLORS.candleUp : COLORS.candleDown;
      // Wick
      ctx.beginPath();
      ctx.moveTo(x, yH);
      ctx.lineTo(x, yL);
      ctx.stroke();
      // Body
      const bodyTop = Math.min(yO, yC);
      const bodyH = Math.max(1, Math.abs(yC - yO));
      ctx.fillRect(x - candleW / 2, bodyTop, candleW, bodyH);
    }

    // ── Level lines (Call Wall, Put Wall, Gamma Flip) ──
    // Labels are stacked at the right edge; two levels a dollar apart would print on top of each
    // other and neither would be readable. Nudge each new label clear of the ones already drawn.
    const labelYs: number[] = [];
    const freeLabelY = (y: number) => {
      let out = y;
      let guard = 0;
      while (labelYs.some((p) => Math.abs(p - out) < 14) && guard < 12) {
        out += 14;
        guard += 1;
      }
      labelYs.push(out);
      return out;
    };

    const drawLevel = (
      strike: number | null,
      color: string,
      label: string,
      dash: number[] = [],
    ) => {
      if (strike == null || strike <= 0) return;
      const y = yPrice(strike);
      if (y < padding.top || y > padding.top + plotH) return;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash(dash);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + plotW, y);
      ctx.stroke();
      ctx.setLineDash([]);
      // Label badge on right
      ctx.fillStyle = color;
      ctx.font = `600 10px ${MONO_FONT}`;
      ctx.textAlign = "right";
      const labelText = `${label} ${strike.toFixed(0)}`;
      const tw = ctx.measureText(labelText).width;
      const ly = freeLabelY(y);
      ctx.fillRect(padding.left + plotW - tw - 6, ly - 7, tw + 4, 14);
      ctx.fillStyle = ct.background;
      ctx.fillText(labelText, padding.left + plotW - 4, ly + 3);
      ctx.textAlign = "left";
    };

    drawLevel(callWall, COLORS.callWall, "CW");
    drawLevel(putWall, COLORS.putWall, "PW");
    drawLevel(gammaFlip, COLORS.gammaFlip, "FLIP", [5, 3]);

    // ── Spot line ──
    if (displaySpot > 0) {
      const y = yPrice(displaySpot);
      ctx.strokeStyle = COLORS.spot;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + plotW, y);
      ctx.stroke();
      ctx.setLineDash([]);
      // Spot badge
      ctx.fillStyle = COLORS.spot;
      ctx.font = `600 10px ${MONO_FONT}`;
      ctx.textAlign = "left";
      ctx.fillText(`● ${displaySpot.toFixed(2)}`, padding.left + 4, y - 4);
    }

    // ── Profile panel (GEX by strike) ──
    if (gammaData.length > 0) {
      const pPad = { top: padding.top, bottom: padding.bottom, left: 40, right: 8 };
      const pPlotW = profileWidth - pPad.left - pPad.right;
      const pPlotH = chartHeight - pPad.top - pPad.bottom;

      // Use same y-scale as price chart for alignment
      const pYPrice = (p: number) =>
        pPad.top + ((maxPrice - p) / (maxPrice - minPrice)) * pPlotH;

      // Filter to visible range
      const visible = gammaData.filter(
        (b) => b.strike >= minPrice && b.strike <= maxPrice,
      );
      if (visible.length > 0) {
        const maxAbsGex = visible.reduce(
          (m, b) => Math.max(m, Math.abs(b.gex)),
          0,
        );
        if (maxAbsGex > 0) {
          // Zero line (center)
          const zeroX = pPad.left + pPlotW / 2;
          pctx.strokeStyle = ct.border;
          pctx.lineWidth = 1;
          pctx.beginPath();
          pctx.moveTo(zeroX, pPad.top);
          pctx.lineTo(zeroX, pPad.top + pPlotH);
          pctx.stroke();

          // Bars
          const barH = Math.max(1, (pPlotH / visible.length) * 0.7);
          for (const b of visible) {
            const y = pYPrice(b.strike);
            const barLen = (Math.abs(b.gex) / maxAbsGex) * (pPlotW / 2);
            const isPos = b.gex >= 0;
            pctx.fillStyle = isPos ? COLORS.posGex : COLORS.negGex;
            pctx.globalAlpha = 0.75;
            const x = isPos ? zeroX : zeroX - barLen;
            pctx.fillRect(x, y - barH / 2, barLen, barH);
            pctx.globalAlpha = 1;
          }

          // Strike labels down the left gutter. The gutter was already reserved but never drawn,
          // which left the bars floating with no way to read which strike they belonged to.
          pctx.fillStyle = ct.text;
          pctx.font = `9px ${MONO_FONT}`;
          pctx.textAlign = "right";
          const labelStep = Math.max(1, Math.ceil((visible.length * 14) / pPlotH));
          visible.forEach((b, i) => {
            if (i % labelStep !== 0) return;
            pctx.fillText(b.strike.toFixed(0), pPad.left - 4, pYPrice(b.strike) + 3);
          });
          pctx.textAlign = "left";

          // Cumulative GEX curve. The bars show where gamma sits; only the running total shows
          // where it nets to zero — which is the flip the level lines are claiming.
          const ordered = [...visible].sort((a, b) => a.strike - b.strike);
          let cum = 0;
          const cumPoints = ordered.map((b) => {
            cum += b.gex;
            return { strike: b.strike, cum };
          });
          const maxAbsCum = cumPoints.reduce(
            (m, p) => Math.max(m, Math.abs(p.cum)),
            0,
          );
          if (maxAbsCum > 0) {
            pctx.strokeStyle = COLORS.gammaFlip;
            pctx.lineWidth = 1.5;
            pctx.globalAlpha = 0.9;
            pctx.beginPath();
            cumPoints.forEach((p, i) => {
              const x = zeroX + (p.cum / maxAbsCum) * (pPlotW / 2) * 0.9;
              const y = pYPrice(p.strike);
              if (i === 0) pctx.moveTo(x, y);
              else pctx.lineTo(x, y);
            });
            pctx.stroke();
            pctx.globalAlpha = 1;
          }

          // Spot line in profile
          if (displaySpot > 0) {
            const y = pYPrice(displaySpot);
            pctx.strokeStyle = COLORS.spot;
            pctx.lineWidth = 1;
            pctx.setLineDash([2, 2]);
            pctx.beginPath();
            pctx.moveTo(pPad.left, y);
            pctx.lineTo(pPad.left + pPlotW, y);
            pctx.stroke();
            pctx.setLineDash([]);
          }

          // Profile header
          pctx.fillStyle = ct.text;
          pctx.font = `10px ${UI_FONT}`;
          pctx.textAlign = "center";
          pctx.fillText("Net GEX by strike · open interest", profileWidth / 2, 12);
          pctx.fillStyle = COLORS.gammaFlip;
          pctx.fillText("— cumulative", profileWidth / 2, 24);
          pctx.textAlign = "left";
        }
      }
    }

    // ── Time axis labels ──
    ctx.fillStyle = ct.text;
    ctx.font = `10px ${MONO_FONT}`;
    ctx.textAlign = "center";
    const timeSteps = 6;
    for (let i = 0; i <= timeSteps; i++) {
      const t = tMin + (tRange * i) / timeSteps;
      const x = xScale(t);
      ctx.fillText(formatTime(t), x, chartHeight - 6);
    }
  }, [
    candles,
    gammaData,
    callWall,
    putWall,
    gammaFlip,
    displaySpot,
    displaySymbol,
    priceLoading,
    barsError,
    size,
    ct,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Hover handler for tooltip — shared by mouse and touch (mobile embed).
  const updateHover = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas || candles.length === 0) return;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      setHover({ x, y });

      // Find nearest candle
      const padding = { top: 10, right: 60, bottom: 24, left: 8 };
      const profileWidth = Math.min(280, size.w * 0.25);
      const priceWidth = size.w - profileWidth - 12;
      const plotW = priceWidth - padding.left - padding.right;
      const tMin = candles[0].t;
      const tMax = candles[candles.length - 1].t;
      const tRange = Math.max(1, tMax - tMin);
      const t = tMin + ((x - padding.left) / plotW) * tRange;
      const idx = candles.findIndex((c) => c.t >= t);
      const candle = candles[Math.max(0, Math.min(candles.length - 1, idx))];

      if (candle) {
        setTooltip({
          x: clientX,
          y: clientY,
          rows: [
            { label: "Time", value: new Date(candle.t).toLocaleString() },
            { label: "O", value: candle.o.toFixed(2) },
            { label: "H", value: candle.h.toFixed(2) },
            { label: "L", value: candle.l.toFixed(2) },
            { label: "C", value: candle.c.toFixed(2) },
            { label: "Vol", value: candle.v.toFixed(0) },
          ],
        });
      }
    },
    [candles, size.w],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => updateHover(e.clientX, e.clientY),
    [updateHover],
  );

  const handleTouch = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      const t = e.touches[0];
      if (t) updateHover(t.clientX, t.clientY);
    },
    [updateHover],
  );

  const handleMouseLeave = useCallback(() => {
    setHover(null);
    setTooltip(null);
  }, []);

  const loading = priceLoading || gammaLoading || chainLoading;

  // Format helpers
  const fmtGex = (v: number) => {
    const abs = Math.abs(v);
    if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v.toFixed(0);
  };

  const fmtPrice = (v: number | null | undefined) => {
    if (v == null || !Number.isFinite(v) || v <= 0) return "—";
    return v.toFixed(2);
  };

  return (
    <div className="flex flex-col h-full" style={{ background: "var(--elev)", fontFamily: "var(--font-ui)" }}>
      {/* ── Toolbar ── */}
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ background: "var(--elev2)", borderColor: "var(--hair)" }}
      >
        <input
          type="text"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          className="w-20 border rounded px-2 py-1 text-xs font-semibold focus:outline-none"
          style={{
            background: "var(--elev)",
            color: "var(--txt)",
            borderColor: "var(--border)",
            fontFamily: "var(--font-mono)",
          }}
          spellCheck={false}
        />

        <span
          className="text-[10px] hidden lg:inline"
          style={{ color: "var(--muted-2)" }}
          title="Structural: the dealer book from open interest, by expiry — where the walls and the flip sit. For today's trading against those levels, use Real-Time GEX."
        >
          positioning map · open interest
        </span>

        {/* Expiration tabs */}
        <div
          className="mmec-seg"
          role="group"
          style={{ "--n": EXPIRY_TABS.length, "--i": EXPIRY_TABS.findIndex((t) => t.id === expiryTab) } as React.CSSProperties}
        >
          {EXPIRY_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setExpiryTab(tab.id)}
              className={`mmec-seg-btn${expiryTab === tab.id ? " is-active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Interval */}
        <div className="flex items-center gap-1">
          {INTERVALS.map((i) => (
            <button
              key={i}
              onClick={() => setIntervalState(i)}
              className={`mmec-btn is-ghost${interval === i ? " is-active" : ""}`}
            >
              {i}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3 text-xs" style={{ color: "var(--muted)" }}>
          {loading && <span style={{ color: "var(--muted-2)" }}>Loading…</span>}
          <span
            style={{
              color:
                wsStatus === "streaming"
                  ? "var(--up)"
                  : wsStatus === "connected"
                    ? "var(--warn)"
                    : "var(--muted-2)",
            }}
          >
            {wsStatus === "streaming"
              ? "● LIVE"
              : wsStatus === "connected"
                ? "● WS"
                : "○ offline"}
          </span>
        </div>
      </div>

      {/* ── Metrics Bar ── */}
      <div
        className="flex items-center gap-4 px-3 py-1.5 border-b text-xs"
        style={{ background: "var(--elev)", borderColor: "var(--hair)" }}
      >
        <Metric label="Spot" value={fmtPrice(displaySpot)} color={COLORS.spot} />
        <Metric
          label="Net GEX"
          value={fmtGex(netGex)}
          color={regimeColor}
          bold
        />
        <Metric
          label="Regime"
          value={regime}
          color={regimeColor}
        />
        <Metric label="Flip" value={fmtPrice(gammaFlip)} color={COLORS.gammaFlip} />
        <Metric label="Call Wall" value={fmtPrice(callWall)} color={COLORS.callWall} />
        <Metric label="Put Wall" value={fmtPrice(putWall)} color={COLORS.putWall} />
        <span className="ml-auto" style={{ color: "var(--muted-2)" }}>
          {priceLabel} · {displaySymbol}
        </span>
      </div>

      {/* ── Chart Area ── */}
      <div ref={containerRef} className="flex-1 relative min-h-0 flex">
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouch}
          onTouchMove={handleTouch}
          onTouchEnd={handleMouseLeave}
          className="flex-1 cursor-crosshair"
        />
        <canvas ref={profileCanvasRef} className="shrink-0" />

        {/* Tooltip */}
        {tooltip && (
          <div
            className="fixed pointer-events-none z-50 px-3 py-2 text-xs"
            style={{
              left: tooltip.x + 14,
              top: tooltip.y + 14,
              background: "var(--elev)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              boxShadow: "var(--lift-2)",
              color: "var(--txt)",
            }}
          >
            {tooltip.rows.map((r, i) => (
              <div key={i} className="flex gap-2">
                <span style={{ color: "var(--muted-2)" }}>{r.label}</span>
                <span style={{ color: "var(--txt)", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}>{r.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helper components ──────────────────────────────────────────────

function Metric({
  label,
  value,
  color,
  bold,
}: {
  label: string;
  value: string;
  color?: string;
  bold?: boolean;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span style={{ color: "var(--muted-2)" }}>{label}</span>
      <strong
        className={bold ? "text-sm" : ""}
        style={{
          color: color || "var(--txt)",
          fontFamily: "var(--font-mono)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </strong>
    </span>
  );
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  return `${h}:${m}`;
}
